import { v4 as uuid } from 'uuid'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { IconChoices } from '../../../common/Icon'
import { LinkSection } from '../LinkSection'
import { useBostadsratternaLinks } from './useBostadsratternaLinks'
import { useSamfalligheternaLinks } from './useSamfalligheternaLinks'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { societyGuidesQueries } from '../../../../api/society-guides'
import { i18nSocietyType } from '../../../../i18n'
import { notificationsQueries } from '../../../../api/notifications'
import { useFeatures } from '../../../../hooks/useFeatures'

export const useManagementLinks = ({
  societyLockedForUser,
  society,
}: {
  society?: ISocietiesModel
  societyLockedForUser: boolean
}): LinkSection[] => {
  const { translate } = useAppTranslation()
  const bostadsratternaLinks = useBostadsratternaLinks(society)
  const samfalligheternaLinks = useSamfalligheternaLinks(society)
  const authUserId = useAuthenticatedUserId() as string

  const isUserAdmin = isUserAdminInSociety(society, authUserId)
  const { data: getToKnow } = useQuery(
    societyGuidesQueries.getToKnow(society?._id, isUserAdmin)
  )
  const { data: notifications } = useQuery(
    notificationsQueries.getUserUnseenUnreadCount(authUserId)
  )
  const unreadTicketsBoard = useMemo(() => {
    if (!society) return false
    const tickets = notifications?.ticketsBoard
    if (!tickets) return false
    return !!tickets?.[society?._id]
  }, [notifications?.ticketsBoard, society])
  const { enabledFeatures } = useFeatures()

  const id = society?._id

  const managementLinks: LinkSection[] = [
    {
      id: uuid(),
      title: translate('societySideBar.sections.board.managementTitle', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      }),
      displayNotificationBadge: unreadTicketsBoard,
      sublinks: [
        ...[
          !enabledFeatures.ticket
            ? undefined
            : {
                title: translate('ticketBoardView.title'),
                icon: IconChoices.ASSIGNMENT,
                url: reverseUrl('management:tickets', { id }),
                disabled: societyLockedForUser,
                displayNotificationBadge: unreadTicketsBoard,
              },
        ],
        {
          title: translate('managementDocumentsView.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          icon: IconChoices.FOLDER,
          url: reverseUrl('management:documents', { id }),
          disabled: societyLockedForUser,
        },
        {
          title: translate('boardContactsView.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          icon: IconChoices.GROUP,
          url: reverseUrl('management:contacts', { id }),
          disabled: societyLockedForUser,
        },
        {
          title: translate('boardCalendarView.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          icon: IconChoices.CALENDAR,
          url: reverseUrl('management:calendar', { id }),
          disabled: societyLockedForUser,
        },
        {
          title: translate('societyContractsView.title'),
          icon: IconChoices.TASK,
          url: reverseUrl('management:contracts', { id }),
        },
        {
          title: translate('societyBoardMembersView.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          icon: IconChoices.GROUPS,
          url: reverseUrl('management:board', { id }),
        },
        {
          title: translate('societyStatisticsExportsView.title'),
          icon: IconChoices.EQUALIZER,
          url: reverseUrl('management:statistics-exports', { id }),
          disabled: societyLockedForUser,
        },
        {
          title: translate('societyInformationView.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          icon: IconChoices.INFORMATION,
          url: reverseUrl('management:information', { id }),
        },
        {
          title: translate('gdprView.title'),
          icon: IconChoices.LOCK,
          url: reverseUrl('management:gdpr', { id }),
          disabled: societyLockedForUser,
        },
      ],
    },
    ...(isUserAdmin
      ? [
          {
            id: uuid(),
            title: translate('societySideBar.sections.board.adminTitle'),
            sublinks: [
              {
                title: translate('invoiceView.title'),
                icon: IconChoices.PAYMENT,
                url: reverseUrl('management:billing', { id }),
              },
              {
                title: translate('registerView.title'),
                icon: IconChoices.GROUPS,
                url: reverseUrl('management:register', { id }),
                disabled: societyLockedForUser,
              },
              {
                title: translate('managementBinderView.title'),
                icon: IconChoices.BOOK,
                url: reverseUrl('management:binder', { id }),
                disabled: societyLockedForUser,
              },
              {
                title: translate('adminRightsView.title'),
                icon: IconChoices.BOARD_PORTAL,
                url: reverseUrl('management:admin-rights', { id }),
                disabled: societyLockedForUser,
              },
              {
                title: translate('societyWebsiteView.title'),
                icon: IconChoices.DOMAIN,
                url: reverseUrl('management:website', { id }),
                disabled: societyLockedForUser,
              },
              {
                title: translate('societyBlockedUsersView.title'),
                icon: IconChoices.BLOCKED_USER,
                url: reverseUrl('management:blocked-users', { id }),
                disabled: societyLockedForUser,
              },
              {
                title: translate('societySideBar.callouts.getToKnow.title'),
                icon: IconChoices.CHECKMARK_ROUND,
                url: reverseUrl('management:get-to-know', { id }),
              },
              ...(getToKnow?.completed
                ? [
                    {
                      title: translate('gettingStartedScreen.navigation'),
                      icon: IconChoices.CHECKMARK_ROUND,
                      url: reverseUrl('management:getting-started', { id }),
                    },
                  ]
                : []),
              {
                title: translate('societySettingsView.title'),
                icon: IconChoices.TUNE,
                url: reverseUrl('management:settings', { id }),
                disabled: societyLockedForUser,
              },
            ],
          },
        ]
      : []),
    ...bostadsratternaLinks,
    ...samfalligheternaLinks,
  ]
  return managementLinks
}
